import {
  Avatar,
  Button,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast
} from '@chakra-ui/react'
import moment from 'moment'
import { CaretRight } from 'phosphor-react'
import { useTranslation } from 'react-i18next'

import { useCreateResponsibleMedicalRecordLinkMutation } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/presentation/hooks/useCreateResponsibleMedicalRecordLinkMutation'
import { useSetResponsibleMedicalRecordExpiredMutation } from '@/modules/medicalRecord/modules/responsibleMedicalRecord/presentation/hooks/useSetResponsibleMedicalRecordExpiredMutation'
import { copyResponsibleMedicalRecordFillURL } from '@/therapist/pages/medicalRecord/components/profileClient/utils/copyResponsibleMedicalRecordFillURL'
import TrashDeleteIcon from '@/ui/assets/TrashDeleteIcon'
import TableBodyLoading from '@/ui/components/Status/Loading/TableBodyLoading'
import TableRowNotFound from '@/ui/components/Status/NotFound/TableRowNotFound'

import { SortableTh } from '../../../../Photos/components/SortableTh'
import { useSortableData } from '../../../../Photos/contexts/useSortableData'
import { MedicalRecordReviewType } from '../../constants/MedicalRecordReviewType'
import { useMedicalRecordManagementContext } from '../../contexts/MedicalRecordManagementContext'

export default function MedicalRecordManagementTable() {
  const { t } = useTranslation()
  const toast = useToast()
  const {
    listResponsibleMedicalRecordQuery,
    filter,
    onReviewViewOpen,
    setSelectedItemId,
    selectedItemId,
    setPatient,
    setReviewMode
  } = useMedicalRecordManagementContext()

  const { handleSort, sortDirection, sortKey, sortedData } = useSortableData(
    listResponsibleMedicalRecordQuery?.data ?? [],
    (item, key) => {
      if (key === 'personaInfoSummary')
        return (
          item.personaInfoSummary?.avatarName ??
          item.personaInfoSummary.username
        )
      return item[key] ?? undefined
    }
  )

  const setResponsibleMedicalRecordExpiredMutation =
    useSetResponsibleMedicalRecordExpiredMutation({
      onSuccess() {
        toast({
          status: 'success',
          description: t('medicalRecordManagement.table.actions.expire.success')
        })
      },
      onError() {
        toast({
          status: 'error',
          description: t('medicalRecordManagement.table.actions.expire.error')
        })
      }
    })

  const createResponsibleMedicalRecordLinkMutation =
    useCreateResponsibleMedicalRecordLinkMutation({
      onSuccess: ({ uuid }) => {
        toast({
          status: 'success',
          description: t('medicalRecordManagement.table.actions.resend.success')
        })
        copyResponsibleMedicalRecordFillURL(uuid)
      },
      onError: () => {
        toast({
          status: 'error',
          description: t('medicalRecordManagement.table.actions.resend.error')
        })
      }
    })

  return (
    <TableContainer
      borderRadius={'md'}
      border={'solid 1px #DEE2E6'}
      w={'full'}
      maxH={'full'}
      overflowY={'auto'}
    >
      <Table>
        <Thead
          position={'sticky'}
          top={0}
          backgroundColor={'gray.50'}
          zIndex={1}
        >
          <Tr
            backgroundColor={'#F5F5FF'}
            css={`
              & > * {
                border-bottom: solid 1px #dee2e6 !important;
                padding-top: 1rem !important;
                padding-bottom: 1rem !important;
                color: #868e96 !important;
                font-weight: 400 !important;
              }
            `}
          >
            <SortableTh
              currentSortKey={sortKey}
              onSort={() => handleSort('personaInfoSummary')}
              sortDirection={sortDirection}
              sortKey={'personaInfoSummary'}
            >
              <Text
                fontSize={'0.875rem'}
                color={'custom.gray.300'}
                fontWeight={'400'}
                textTransform={'lowercase'}
                _firstLetter={{
                  textTransform: 'uppercase'
                }}
                letterSpacing={0}
              >
                {t('medicalRecordManagement.table.headers.patient')}
              </Text>
            </SortableTh>
            <SortableTh
              currentSortKey={sortKey}
              onSort={() => handleSort('send')}
              sortDirection={sortDirection}
              sortKey={'send'}
            >
              <Text
                fontSize={'0.875rem'}
                color={'custom.gray.300'}
                fontWeight={'400'}
                textTransform={'lowercase'}
                _firstLetter={{
                  textTransform: 'uppercase'
                }}
                letterSpacing={0}
              >
                {t('medicalRecordManagement.table.headers.sendDate')}
              </Text>
            </SortableTh>
            {(filter.status === 'WAITING_FOR_REVIEW' ||
              filter.status === 'COMPLETE') && (
              <SortableTh
                currentSortKey={sortKey}
                onSort={() => handleSort('record')}
                sortDirection={sortDirection}
                sortKey={'record'}
              >
                <Text
                  fontSize={'0.875rem'}
                  color={'custom.gray.300'}
                  fontWeight={'400'}
                  textTransform={'lowercase'}
                  _firstLetter={{
                    textTransform: 'uppercase'
                  }}
                  letterSpacing={0}
                >
                  {t('medicalRecordManagement.table.headers.fillDate')}
                </Text>
              </SortableTh>
            )}
            {filter.status === 'COMPLETE' && (
              <SortableTh
                currentSortKey={sortKey}
                onSort={() => handleSort('completed')}
                sortDirection={sortDirection}
                sortKey={'completed'}
              >
                <Text
                  fontSize={'0.875rem'}
                  color={'custom.gray.300'}
                  fontWeight={'400'}
                  textTransform={'lowercase'}
                  _firstLetter={{
                    textTransform: 'uppercase'
                  }}
                  letterSpacing={0}
                >
                  {t('medicalRecordManagement.table.headers.completeDate')}
                </Text>
              </SortableTh>
            )}
            {filter.status === 'EXPIRED' && (
              <SortableTh
                currentSortKey={sortKey}
                onSort={() => handleSort('record')}
                sortDirection={sortDirection}
                sortKey={'record'}
              >
                <Text
                  fontSize={'0.875rem'}
                  color={'custom.gray.300'}
                  fontWeight={'400'}
                  textTransform={'lowercase'}
                  _firstLetter={{
                    textTransform: 'uppercase'
                  }}
                  letterSpacing={0}
                >
                  {t('medicalRecordManagement.table.headers.expirationDate')}
                </Text>
              </SortableTh>
            )}
            <Th>
              <Text
                fontSize={'0.875rem'}
                color={'custom.gray.300'}
                fontWeight={'400'}
                textTransform={'lowercase'}
                _firstLetter={{
                  textTransform: 'uppercase'
                }}
                letterSpacing={0}
              >
                {t('medicalRecordManagement.table.headers.action')}
              </Text>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {!listResponsibleMedicalRecordQuery ||
          listResponsibleMedicalRecordQuery.isFetching ? (
            <TableBodyLoading
              columns={
                filter.status === 'COMPLETE'
                  ? 5
                  : filter.status === 'SENT'
                    ? 3
                    : 4
              }
              rows={3}
            />
          ) : listResponsibleMedicalRecordQuery.isError ? (
            <>Erro</>
          ) : !listResponsibleMedicalRecordQuery.data ||
            listResponsibleMedicalRecordQuery.data.length === 0 ? (
            <TableRowNotFound />
          ) : (
            sortedData.map(
              ({ completed, id, personaInfoSummary, record, send }) => {
                return (
                  <Tr
                    key={id}
                    css={`
                      & > * {
                        padding-top: 0.5rem !important;
                        padding-bottom: 0.5rem !important;
                        border-top: solid 1px #dee2e6 !important;
                        border-bottom: solid 1px #dee2e6 !important;
                      }
                    `}
                  >
                    <Td>
                      <Flex alignItems={'center'} gap={'2'}>
                        <Avatar
                          src={personaInfoSummary.avatarUrl}
                          name={
                            personaInfoSummary.avatarName ??
                            personaInfoSummary.username
                          }
                          size={'xs'}
                        />
                        <Text
                          fontSize={'0.875rem'}
                          color={'custom.gray.300'}
                          fontWeight={'400'}
                        >
                          {personaInfoSummary?.avatarName ??
                            personaInfoSummary.username}
                        </Text>
                      </Flex>
                    </Td>
                    <Td
                      fontSize={'0.875rem'}
                      color={'custom.gray.300'}
                      fontWeight={'400'}
                    >
                      {moment(send, 'YYYY-MM-DD').format('DD/MM/YYYY')}
                    </Td>
                    {(filter.status === 'WAITING_FOR_REVIEW' ||
                      filter.status === 'COMPLETE' ||
                      filter.status === 'EXPIRED') && (
                      <Td
                        fontSize={'0.875rem'}
                        color={'custom.gray.300'}
                        fontWeight={'400'}
                      >
                        {record
                          ? moment(record, 'YYYY-MM-DD').format('DD/MM/YYYY')
                          : '-'}
                      </Td>
                    )}
                    {filter.status === 'COMPLETE' && (
                      <Td
                        fontSize={'0.875rem'}
                        color={'custom.gray.300'}
                        fontWeight={'400'}
                      >
                        {completed
                          ? moment(completed, 'YYYY-MM-DD').format('DD/MM/YYYY')
                          : '-'}
                      </Td>
                    )}
                    <Td w={'200px'}>
                      {filter.status === 'WAITING_FOR_REVIEW' ? (
                        <Button
                          colorScheme={'blue'}
                          color={'custom.blue.darker'}
                          variant={'link'}
                          fontSize={'1rem'}
                          fontWeight={'500'}
                          gap={'2'}
                          onClick={() => {
                            setSelectedItemId(id)
                            setReviewMode(MedicalRecordReviewType.enum.REVIEW)
                            setPatient(personaInfoSummary)
                            onReviewViewOpen()
                          }}
                        >
                          {t(
                            'medicalRecordManagement.table.data.actions.review'
                          )}
                          <CaretRight />
                        </Button>
                      ) : filter.status === 'SENT' ? (
                        <Button
                          colorScheme={'red'}
                          color={'custom.red.dark'}
                          variant={'link'}
                          fontSize={'1rem'}
                          fontWeight={'500'}
                          gap={'2'}
                          isLoading={
                            setResponsibleMedicalRecordExpiredMutation.isPending &&
                            selectedItemId === id
                          }
                          isDisabled={
                            setResponsibleMedicalRecordExpiredMutation.isPending
                          }
                          onClick={() => {
                            setSelectedItemId(id)
                            setResponsibleMedicalRecordExpiredMutation.mutate({
                              id
                            })
                          }}
                        >
                          {t(
                            'medicalRecordManagement.table.data.actions.expire'
                          )}
                          <TrashDeleteIcon />
                        </Button>
                      ) : filter.status === 'EXPIRED' ? (
                        <Button
                          colorScheme={'blue'}
                          color={'custom.blue.darker'}
                          variant={'link'}
                          fontSize={'1rem'}
                          fontWeight={'500'}
                          gap={'2'}
                          isLoading={
                            createResponsibleMedicalRecordLinkMutation.isPending &&
                            selectedItemId === id
                          }
                          isDisabled={
                            createResponsibleMedicalRecordLinkMutation.isPending
                          }
                          onClick={() => {
                            setSelectedItemId(id)
                            createResponsibleMedicalRecordLinkMutation.mutate({
                              patientId: personaInfoSummary.userId
                            })
                          }}
                        >
                          {t(
                            'medicalRecordManagement.table.data.actions.resend'
                          )}
                          <CaretRight />
                        </Button>
                      ) : filter.status === 'COMPLETE' ? (
                        <Button
                          colorScheme={'blue'}
                          color={'custom.blue.darker'}
                          variant={'link'}
                          fontSize={'1rem'}
                          fontWeight={'500'}
                          gap={'2'}
                          onClick={() => {
                            setSelectedItemId(id)
                            setReviewMode(MedicalRecordReviewType.enum.VIEW)
                            setPatient(personaInfoSummary)
                            onReviewViewOpen()
                          }}
                        >
                          {t('medicalRecordManagement.table.data.actions.see')}
                          <CaretRight />
                        </Button>
                      ) : (
                        <></>
                      )}
                    </Td>
                  </Tr>
                )
              }
            )
          )}
        </Tbody>
      </Table>
    </TableContainer>
  )
}
